<template>
  <BaseDialog
    :is-open="isRevealed"
    :disable-click-outside="step === 'pin'"
    @close="cancel"
  >
    <div
      class="flex max-h-full min-h-0 min-w-56 flex-1 flex-col"
    >
      <template v-if="step === 'select'">
        <h3
          class="mb-1 text-base font-semibold text-background-fg"
        >
          Switch Cashier
        </h3>
        <p class="mb-3 text-sm text-background-fg/60">
          Select who is taking over
        </p>

        <div
          v-if="selectError"
          class="mb-3 rounded-lg border border-error bg-error/10 px-4 py-2 text-center text-sm font-medium text-error"
        >
          {{ selectError }}
        </div>

        <div class="flex-1 overflow-y-auto">
          <ul
            v-if="cashiers.length"
            class="divide-y divide-background-fg/5"
          >
            <li
              v-for="cashier in cashiers"
              :key="cashier.id"
            >
              <button
                type="button"
                class="relative flex w-full items-center space-x-3 py-3 pr-4 active:bg-background-fg/5"
                @click="handleSelectCashier(cashier)"
              >
                <BaseInnerLoading
                  v-if="isLoggingInCashierId === cashier.id"
                />
                <div
                  class="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white"
                  :style="{
                    backgroundColor:
                      cashier.color ?? undefined,
                  }"
                >
                  {{ buildShortString(cashier.username) }}
                </div>
                <div class="flex flex-1 items-center">
                  <div
                    class="line-clamp-2 text-left text-sm font-semibold"
                  >
                    {{ cashier.username }}
                  </div>
                </div>
              </button>
            </li>
          </ul>
          <div
            v-else
            class="my-5 text-center font-medium text-background-fg/70"
          >
            No cashiers to display
          </div>
        </div>

        <div
          v-if="allowLoginWithoutCashier"
          class="mt-3 flex flex-col space-y-3"
        >
          <ActionButtonPrimary
            class="w-full"
            @click="handleSkipCashierLogIn"
          >
            Continue Without a Cashier
          </ActionButtonPrimary>
          <ActionButtonSecondary
            class="w-full"
            @click="cancel"
          >
            Cancel
          </ActionButtonSecondary>
        </div>
        <div v-else class="mt-3 flex flex-col">
          <ActionButtonSecondary
            class="w-full"
            @click="cancel"
          >
            Cancel
          </ActionButtonSecondary>
        </div>
      </template>

      <template
        v-else-if="step === 'pin' && selectedCashier"
      >
        <div class="mb-5 flex items-center space-x-2">
          <button
            type="button"
            class="rounded-full p-1.5 text-background-fg/60 active:opacity-60"
            @click="goBack"
          >
            <IconChevronLeft class="size-5" />
          </button>
          <div>
            <h3
              class="text-base font-semibold text-background-fg"
            >
              {{ selectedCashier.username }}
            </h3>
            <p class="text-sm text-background-fg/60">
              Enter PIN to switch
            </p>
          </div>
        </div>

        <div class="flex flex-col items-center pb-2">
          <div
            v-if="pinError"
            class="mb-4 w-full rounded-lg border border-error bg-error/10 px-4 py-2 text-center text-sm font-medium text-error"
          >
            {{ pinError }}
          </div>
          <PinInput
            :model-value="pin"
            @update:model-value="pin = $event"
          />
        </div>

        <div class="mt-5 flex flex-col">
          <ActionButtonSecondary
            class="w-full"
            @click="cancel"
          >
            Cancel
          </ActionButtonSecondary>
        </div>
      </template>
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
import { IconChevronLeft } from "@/icons"
import { logInAsCashier } from "@/services/logInAsCashier"
import { useCashiers } from "@/services/cashiers/listCashiers"
import { useConfirmDialog } from "@vueuse/core"
import type { Cashier } from "@/services/sharedTypes/cashiers"
import { ApiError } from "@/services/api"

const { setBaseToken } = useAuth()
const { startLoading, stopLoading } =
  usePageBlockingLoading()
const { data: cashiers } = useCashiers()
const { allowLoginWithoutCashier } = useTenant()

const { reveal, isRevealed, confirm, cancel, onReveal } =
  useConfirmDialog()

defineExpose({ reveal })

const step = ref<"select" | "pin">("select")
const selectedCashier = ref<Cashier>()
const pin = ref("")
const pinError = ref<string | null>(null)
const selectError = ref<string | null>(null)
const isLoggingInCashierId = ref<string | null>(null)

onReveal(() => {
  step.value = "select"
  selectedCashier.value = undefined
  pin.value = ""
  pinError.value = null
  selectError.value = null
  isLoggingInCashierId.value = null
})

const goBack = () => {
  step.value = "select"
  selectedCashier.value = undefined
  pin.value = ""
  pinError.value = null
}

const handleSelectCashier = async (cashier: Cashier) => {
  selectError.value = null

  if (!cashier.pin) {
    isLoggingInCashierId.value = cashier.id
    startLoading()
    try {
      const body = await logInAsCashier({
        cashierId: cashier.id,
        pin: null,
      })
      setBaseToken(body.accessToken)
      confirm()
    } catch {
      selectError.value = `Failed to switch to ${cashier.username}`
    } finally {
      isLoggingInCashierId.value = null
      stopLoading()
    }
  } else {
    selectedCashier.value = cashier
    step.value = "pin"
  }
}

const handleSkipCashierLogIn = async () => {
  selectError.value = null
  startLoading()
  try {
    const body = await logInAsCashier({
      cashierId: null,
      pin: null,
    })
    setBaseToken(body.accessToken)
    confirm()
  } catch (error) {
    if (
      error instanceof ApiError &&
      error.code === "login-without-cashier-not-allowed"
    ) {
      selectError.value =
        "Logging in without selecting a cashier is not enabled"
    } else {
      selectError.value =
        "Failed to continue without a cashier"
    }
  } finally {
    stopLoading()
  }
}

const pinIsComplete = computed(() => pin.value.length === 4)

whenever(pinIsComplete, () => submitPin())

const submitPin = async () => {
  if (
    !selectedCashier.value ||
    !pinIsComplete.value ||
    isLoggingInCashierId.value
  )
    return

  pinError.value = null
  isLoggingInCashierId.value = selectedCashier.value.id
  startLoading()
  try {
    const body = await logInAsCashier({
      cashierId: selectedCashier.value.id,
      pin: pin.value,
    })
    setBaseToken(body.accessToken)
    confirm()
  } catch (error) {
    pin.value = ""
    if (
      error instanceof ApiError &&
      error.code === "pin-incorrect"
    ) {
      pinError.value = "Incorrect PIN, please try again"
    } else {
      pinError.value = "Something went wrong"
    }
  } finally {
    isLoggingInCashierId.value = null
    stopLoading()
  }
}
</script>
